import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage from '../../components/LegalPage';
import legalConfig from '../../lib/legalConfig';
import { useLanguage } from '../../context/LanguageContext';

const META_DESC =
  'CosmoEstates Subscription, Boost & Refund Terms: how Pro plans, Boost packages, and CPL fees work, plus your 14-day right to withdraw as a consumer under Cyprus Consumer Protection Law 112(I)/2021.';

export default function BillingPage() {
  const { t } = useLanguage();
  return (
    <LegalPage
      title={t('legal.docs.billingTitle')}
      effectiveDate={legalConfig.effectiveDate}
      metaTitle="Subscription, Boost & Refund Terms | CosmoEstates"
      metaDescription={META_DESC}
    >
      <div className="legal-meta">
        <p>
          <strong>CosmoEstates</strong> — {legalConfig.entityName}
        </p>
        <p>
          <strong>Website:</strong>{' '}
          <a href={legalConfig.websiteUrl}>{legalConfig.websiteUrl}</a>
        </p>
        <p>
          <strong>Billing contact:</strong> {legalConfig.contactEmail}
        </p>
      </div>

      <p className="legal-draft-note">
        Draft for review by Cyprus-qualified counsel. Not legal advice. These terms supplement
        the <Link to="/terms">Terms &amp; Conditions</Link> and are written with the Cyprus
        Consumer Protection Law 112(I)/2021 (including the 14-day right of withdrawal for
        distance contracts) in mind.
      </p>

      <h2>1. What these terms cover</h2>
      <p>These terms apply to paid features on CosmoEstates, including:</p>
      <ul>
        <li>
          <strong>CosmoEstates Pro</strong> subscriptions (monthly, quarterly, yearly);
        </li>
        <li>
          <strong>Boost Listing</strong> packages;
        </li>
        <li>
          <strong>Professional / partner fees</strong> and{' '}
          <strong>cost-per-lead (CPL)</strong> charges; and
        </li>
        <li>any other paid feature or advertising we offer.</li>
      </ul>

      <h2>2. CosmoEstates Pro</h2>
      <p>
        2.1 <strong>What you get.</strong> Pro currently includes [Price-Drop Email alerts,
        Similar-Listing alerts, Priority Inbox Placement, and Neighbourhood Intelligence].
        Features may be added, changed, or removed; we will give reasonable notice of material
        reductions.
      </p>
      <p>
        2.2 <strong>Plans and price.</strong> Pro is offered on <strong>monthly</strong>,{' '}
        <strong>quarterly</strong>, and <strong>yearly</strong> plans at the prices shown at
        checkout.
      </p>
      <p>
        2.3 <strong>Auto-renewal.</strong> Unless stated otherwise, subscriptions{' '}
        <strong>renew automatically</strong> at the end of each period at the then-current
        price, until cancelled. We will [confirm if/how] remind you before renewal where
        required.
      </p>
      <p>
        2.4 <strong>Cancellation.</strong> You can cancel at any time via your account or by
        contacting {legalConfig.contactEmail}. Cancellation stops the next renewal; your Pro
        access continues until the end of the current paid period. Except where the law
        requires otherwise (see section 5), part-period fees are not refunded.
      </p>

      <h2>3. Boost Listing</h2>
      <p>
        3.1 A Boost makes a listing more visible — for example eligibility for the featured
        listings carousel — for a set duration (currently <strong>14 days</strong> for{' '}
        <strong>one listing</strong> for private sellers).
      </p>
      <p>
        3.2 A Boost is a{' '}
        <strong>digital service that begins as soon as the boost goes live</strong>. Boost is
        non-renewing unless stated. Visibility/placement depends on other listings and is not
        guaranteed to produce enquiries or a sale.
      </p>
      <p>
        3.3 Pricing: {legalConfig.priceBoost}. See section 5 for withdrawal/refund rules, which
        are affected by the fact the service starts immediately.
      </p>

      <h2>4. Professional fees and CPL</h2>
      <p>
        4.1 Independent agents, partner agents, and contractors pay fees as agreed (for example
        a flat fee, a per-agent fee, and/or CPL charges per lead). The specific commercial
        terms — fee amounts, billing, lead volumes, invalid-lead credits, term, and termination
        — are set out in the separate partner/agent agreement or order form.
      </p>
      <p>
        4.2 <strong>Business customers (B2B).</strong> The 14-day consumer withdrawal right in
        section 5 generally applies to <strong>consumers</strong>, not to businesses buying in
        a business capacity. Professional/partner fees and CPL charges are typically
        non-refundable once leads or services are delivered, except as set out in the partner
        agreement.
      </p>

      <h2>5. Your right to cancel (consumers) — 14-day withdrawal</h2>
      <p>
        5.1 If you are a <strong>consumer</strong> in the EU, you generally have the right to
        withdraw from a distance (online) contract within <strong>14 days</strong> of entering
        into it, under the Cyprus Consumer Protection Law 112(I)/2021,{' '}
        <strong>without giving a reason</strong>.
      </p>
      <p>
        5.2 <strong>Important exception for digital services that start immediately.</strong>{' '}
        If you ask us to start a digital service (such as a Boost or Pro features){' '}
        <strong>before the 14-day period ends</strong>, and you acknowledge this, then:
      </p>
      <ul>
        <li>
          (a) you may lose the right to withdraw once the service is <strong>fully</strong>{' '}
          performed; and
        </li>
        <li>
          (b) if you withdraw while the service is <strong>partly</strong> performed, you may
          have to pay for the portion already provided, proportionate to the full price.
        </li>
      </ul>
      <p>We will ask for this acknowledgement at checkout where it applies.</p>
      <p>
        5.3 <strong>How to withdraw.</strong> To exercise the right, tell us clearly within the
        14 days by email to {legalConfig.contactEmail}, with enough information for us to
        identify your order. We will refund eligible amounts without undue delay and within 14
        days of being informed, using the same payment method, subject to clause 5.2.
      </p>

      <h2>6. Refunds outside the withdrawal right</h2>
      <p>
        6.1 Beyond the statutory withdrawal right, refunds are at our discretion and as
        required by law.
      </p>
      <p>
        6.2 We will refund or provide a remedy where a paid feature is{' '}
        <strong>not delivered</strong> or is <strong>materially defective</strong> due to our
        fault, in line with your consumer rights.
      </p>
      <p>
        6.3 We do <strong>not</strong> generally refund where: the service has been
        delivered/consumed; under-performance is due to factors outside our control (e.g. low
        market demand for a boosted listing); or where a listing or account is removed/suspended
        for breach of our Terms or policies.
      </p>

      <h2>7. Payments</h2>
      <p>
        7.1 Payments are processed by our third-party payment provider [e.g. Stripe]. By
        purchasing you authorise the charge. You must use a payment method you are entitled to
        use.
      </p>
      <p>
        7.2 Prices are in euro and, where applicable, include VAT as shown. We may change
        prices for future periods on notice; changes do not affect a period already paid for.
      </p>
      <p>7.3 Failed or reversed payments may result in suspension of the paid feature.</p>

      <h2>8. Changes and cancellation by us</h2>
      <p>
        We may change features, plans, or prices on reasonable notice. If we discontinue a paid
        feature you have already paid for, we will provide a pro-rata refund for the unused
        portion unless the change is required by law or caused by your breach.
      </p>

      <h2>9. Contact</h2>
      <p>
        Billing questions, cancellations, and refund requests: {legalConfig.contactEmail}.
      </p>
    </LegalPage>
  );
}
